/**
 * @fileoverview Client-side PDB generation from BMaps model residues.
 *
 * Serializes in-memory model residues (Residue atom groups) to a PdbData /
 * PDB string WITHOUT a bfd-server round-trip. Used by ProLIF/pi-pi detection
 * (ScoringCmds) and by unloaded structure-of-interest alignment
 * (ConnectedDataCmds).
 *
 * @typedef {import('BMapsModel').Residue} Residue
 */

import { formResidueKey, PdbData } from 'BMapsSrc/util/pdb';

/**
 * Build a PdbData from a set of model residues, using each atom's original
 * (untransformed) coordinates. Call .format() on the result for PDB text.
 * @param {Residue[]} residuesIn
 * @returns {PdbData}
 */
export function getPdbDataForResidues(residuesIn) {
    const residues = sortResidues(residuesIn);
    const atomSet = new Set(residues.flatMap((residue) => residue.getAtoms()));
    const pdbRes = residues.map((residue) => ({
        hetflag: residue.residue.hetflag,
        resName: residue.resn,
        chainId: residue.chain,
        seqNum: residue.resi,
        iCode: residue.residue.parentInsertionCode,
        atoms: residue.getAtoms().map((atom) => ({
            atomName: atom.atom,
            altLoc: atom.altLoc,
            x: atom.origX,
            y: atom.origY,
            z: atom.origZ,
            element: atom.elem,
            connections: getAtomConnectionsForPdb(atom, atomSet),
        })),
    })).filter(Boolean);
    return new PdbData({ residues: pdbRes });
}

function getAtomConnectionsForPdb(atom, allowedAtoms) {
    const relevantAtom = (a) => a.hetflag || ['C', 'N', 'P', 'O3*'].includes(a.atom) || a.elem === 'S';
    if (!relevantAtom(atom)) return [];
    const connections = atom.bondedAtoms
        .filter((a) => !allowedAtoms || allowedAtoms.has(a))
        .filter(relevantAtom).map((oat) => ({
            resKey: formResidueKey(oat.resn, oat.resi, oat.chain, { iCode: oat.iCode }),
            atomName: oat.atom,
            altLoc: oat.altLoc,
        }));
    return connections;
}

/**
 * Sort residues by chain, sequence number, and insertion code.
 * @param {Residue[]} residuesIn
 * @returns {Residue[]}
 */
function sortResidues(residuesIn) {
    const residues = [...residuesIn];
    residues.sort((a, b) => {
        if (a.chain !== b.chain) return a.chain.localeCompare(b.chain);
        if (a.resi !== b.resi) return a.resi - b.resi;
        if (a.residue.parentInsertionCode !== b.residue.parentInsertionCode) {
            if (a.residue.parentInsertionCode == null) return -1;
            return a.residue.parentInsertionCode.localeCompare(b.residue.parentInsertionCode);
        }
        return 0;
    });
    return residues;
}
